import React, { useState, useEffect, useCallback } from "react";
import { getPendingConfirmations, confirmUsageLog } from "./inventoryApi";

export default function ConfirmInventoryUsageModal({ technicianId, campId, onClose, onSuccess }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPendingConfirmations(technicianId);
      // Filter to current camp if campId provided
      const filtered = campId
        ? (data.results || data).filter(c => String(c.camp) === String(campId))
        : (data.results || data);
      setItems(filtered);
    } catch {
      setError("Failed to load pending confirmations.");
    } finally {
      setLoading(false);
    }
  }, [technicianId, campId]);

  useEffect(() => { load(); }, [load]);

  const handleConfirmAll = async () => {
    if (!items.length) return;
    setConfirming(true);
    try {
      await Promise.all(
        items.map(item =>
          confirmUsageLog({ usage_log: item.usage_log, remarks })
        )
      );
      setSuccessMsg("✓ All inventory usage confirmed successfully!");
      setTimeout(() => onSuccess(), 1800);
    } catch {
      setError("Failed to confirm some items. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Confirm Inventory Usage</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Review items below and confirm your inventory usage for this camp.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {successMsg && (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-sm font-medium mb-4">
              {successMsg}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3" style={{ borderColor: "#11a8a4" }} />
              <p className="text-gray-500 text-sm">Loading pending items...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-lg font-medium">No pending confirmations</p>
              <p className="text-sm mt-1">All inventory usage is already confirmed.</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 mb-5">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                      <th className="px-4 py-3 text-left">Item Name</th>
                      <th className="px-4 py-3 text-left">Service</th>
                      <th className="px-4 py-3 text-center">Qty Used</th>
                      <th className="px-4 py-3 text-center">Remaining</th>
                      <th className="px-4 py-3 text-center">Source</th>
                      <th className="px-4 py-3 text-left">Date & Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {item.item_name}
                          <span className="block text-xs text-gray-400">{item.item_code}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{item.service_name || "—"}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-semibold text-gray-900">{item.quantity_used}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className="font-semibold"
                            style={{ color: item.quantity_remaining <= 5 ? "#ef4444" : "#111827" }}
                          >
                            {item.quantity_remaining}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              background: item.source === "AUTO" ? "#dbeafe" : "#fef9c3",
                              color: item.source === "AUTO" ? "#1d4ed8" : "#92400e",
                            }}
                          >
                            {item.source === "AUTO" ? "Automatic" : "Manual"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                            {new Date(item.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Remarks (optional — applies to all items)
                </label>
                <textarea
                  rows={2}
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  placeholder="Any notes about this confirmation..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{ outlineColor: "#11a8a4" }}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t flex justify-between items-center bg-gray-50">
          <span className="text-sm text-gray-500">
            {items.length} item{items.length !== 1 ? "s" : ""} pending confirmation
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmAll}
              disabled={confirming || loading || items.length === 0}
              className="px-6 py-2 text-sm font-semibold text-white rounded-md transition"
              style={{
                background: confirming || items.length === 0 ? "#9ca3af" : "#11a8a4",
                cursor: confirming || items.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {confirming ? "Confirming..." : `✓ Confirm Inventory Usage (${items.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}